import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import BvIcon from 'volta/components/bv-icon';
import BvInline from 'volta/components/bv-inline';
import BvUnstyledBtn from 'volta/components/bv-unstyled-btn';
import isPresent from 'volta/helpers/is-present';
import stopPropagation from 'volta/helpers/stop-propagation';
import autofocus from 'volta/modifiers/autofocus';

export interface BvAppSearchSearchFieldSignature {
  Args: {
    query?: string;
    placeholder?: string;
    onSearch?: (value: string) => void;
    onClear?: () => void;
  };
  Element: HTMLElement;
}

export default class BvAppSearchSearchFieldComponent extends Component<BvAppSearchSearchFieldSignature> {
  @ref('inputNode')
  inputNode: HTMLInputElement | null = null;

  searchId = `${guidFor(this)}BvAppSearchField`;

  @action
  handleClear() {
    this.cleanInput('focus');
  }

  @action
  handleSearch(event: Event) {
    if (!event) {
      return;
    }
    const { currentTarget } = event;
    this.args.onSearch?.((currentTarget as HTMLInputElement)?.value ?? '');
  }

  @action
  focusInput() {
    if (this.inputNode != null) {
      this.inputNode.focus();
    }
  }

  private cleanInput(method: 'focus' | 'blur') {
    if (this.inputNode != null) {
      this.inputNode.value = '';
      this.args.onSearch?.('');
      this.inputNode[method]();
    }
  }

  <template>
    <BvInline @wrap={{false}} @gap='2' class='bv-app-search-field' ...attributes>
      <BvIcon class='bv-app-search-field__icon' @glyph='search' @color='gray' />
      <label class='u-visually-hidden' for={{this.searchId}}>
        {{@placeholder}}
      </label>
      <input
        id={{this.searchId}}
        class='bv-app-search-field__input'
        placeholder={{@placeholder}}
        type='search'
        autoCapitalize='off'
        autoComplete='off'
        autoCorrect='off'
        value={{@query}}
        {{createRef 'inputNode' bucket=this}}
        {{on 'input' this.handleSearch}}
        {{autofocus true}}
        {{! template-lint-disable no-at-ember-render-modifiers }}
        {{didUpdate this.focusInput @query}}
      />
      {{! Clear button}}
      {{#if (isPresent @query)}}
        <BvUnstyledBtn
          @onClick={{stopPropagation @onClear}}
          aria-label={{t 'clear'}}
          class='bv-app-search-field__clear'
        >
          <BvIcon @glyph='close-circle' />
        </BvUnstyledBtn>
      {{/if}}
    </BvInline>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvAppSearch::SearchField': typeof BvAppSearchSearchFieldComponent;
    'bv-app-search/search-field': typeof BvAppSearchSearchFieldComponent;
  }
}
